/**
 * AutoSaveContext
 * Provides auto-save functionality to child components
 * Allows marking content as dirty when node data changes
 */

import React, { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';

export interface AutoSaveContextValue {
  /** Mark the flow as having unsaved changes */
  markDirty: () => void;
  /** Current dirty state */
  isDirty: boolean;
  /** Current save status */
  status: 'idle' | 'saving' | 'saved' | 'error';
}

const AutoSaveContext = createContext<AutoSaveContextValue | null>(null);

export interface AutoSaveProviderProps {
  children: ReactNode;
  /** Called when content should be marked as dirty */
  onMarkDirty: () => void;
  /** Current dirty state */
  isDirty: boolean;
  /** Current save status */
  status: 'idle' | 'saving' | 'saved' | 'error';
}

export const AutoSaveProvider: React.FC<AutoSaveProviderProps> = ({
  children,
  onMarkDirty,
  isDirty,
  status,
}) => {
  const markDirty = useCallback(() => {
    onMarkDirty();
  }, [onMarkDirty]);

  // Every node adapter — and now UniversalNode, which needs markDirty for the
  // settings footer — subscribes to this. A fresh object literal here would
  // re-render all of them on every render of the editor, not just when the
  // save state actually moves.
  const value = useMemo(
    () => ({ markDirty, isDirty, status }),
    [markDirty, isDirty, status]
  );

  return (
    <AutoSaveContext.Provider value={value}>
      {children}
    </AutoSaveContext.Provider>
  );
};

/**
 * Hook to access auto-save context
 * Returns a no-op function if context is not available (for backwards compatibility)
 */
export const useAutoSaveContext = (): AutoSaveContextValue => {
  const context = useContext(AutoSaveContext);
  
  // Return no-op if context not available
  if (!context) {
    return {
      markDirty: () => {},
      isDirty: false,
      status: 'idle',
    };
  }
  
  return context;
};

export default AutoSaveContext;
